import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable, signal, type Signal, type WritableSignal } from '@angular/core';
import { type Clerk } from '@clerk/clerk-js';
import { readWebRuntimeAuthConfig } from './auth-runtime-config';
import { ClerkCustomerUiLoaderService } from './clerk-customer-ui-loader.service';
import { sanitizeCustomerReturnTo } from './clerk-customer-return-to';

@Injectable({ providedIn: 'root' })
export class ClerkCustomerWebService {
  private readonly document: Document = inject(DOCUMENT);
  private readonly ui: ClerkCustomerUiLoaderService = inject(ClerkCustomerUiLoaderService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly subjectState: WritableSignal<string | null> = signal<string | null>(null);
  private pending: Promise<Clerk> | null = null;
  private instance: Clerk | null = null;

  public readonly subject: Signal<string | null> = this.subjectState.asReadonly();

  public initialize(): Promise<Clerk> {
    this.pending ??= this.createInstance().catch((error: unknown): never => {
      this.pending = null;
      throw error;
    });
    return this.pending;
  }

  public async getToken(): Promise<string | null> {
    const clerk: Clerk = await this.initialize();
    return clerk.session?.getToken() ?? null;
  }

  public async mount(node: HTMLDivElement, action: 'login' | 'signup', returnTo: string): Promise<void> {
    const clerk: Clerk = await this.initialize();
    const target: string = sanitizeCustomerReturnTo(returnTo, this.document.location.origin);
    const query: string = `?returnTo=${encodeURIComponent(target)}`;
    if (clerk.isSignedIn) {
      this.document.location.assign(target);
      return;
    }
    if (!node.isConnected) {
      return;
    }
    if (action === 'signup') {
      clerk.mountSignUp(node, {
        routing: 'hash', signInUrl: `/login${query}`,
        forceRedirectUrl: target, fallbackRedirectUrl: target,
      });
    } else {
      clerk.mountSignIn(node, {
        routing: 'hash', signUpUrl: `/signup${query}`,
        forceRedirectUrl: target, fallbackRedirectUrl: target,
        signUpForceRedirectUrl: target, signUpFallbackRedirectUrl: target,
      });
    }
  }

  public unmount(node: HTMLDivElement, action: 'login' | 'signup'): void {
    if (action === 'signup') {
      this.instance?.unmountSignUp(node);
    } else {
      this.instance?.unmountSignIn(node);
    }
  }

  public async signOut(): Promise<void> {
    const clerk: Clerk = await this.initialize();
    await clerk.signOut();
    this.subjectState.set(null);
  }

  private async createInstance(): Promise<Clerk> {
    const key: string = readWebRuntimeAuthConfig().customerPublishableKey;
    const location: Location = this.document.location;
    if (!/^pk_(test|live)_/.test(key)) {
      throw new Error('Customer Clerk publishable key is missing.');
    }
    if (location.protocol !== 'https:' && !['localhost', '127.0.0.1', '[::1]'].includes(location.hostname)) {
      throw new Error('Customer sign-in requires HTTPS.');
    }
    const module: typeof import('@clerk/clerk-js') = await import('@clerk/clerk-js');
    const clerk: Clerk = new module.Clerk(key);
    const ClerkUI = await this.ui.load(key);
    await clerk.load({ signInUrl: '/login', signUpUrl: '/signup', ui: { ClerkUI } });
    this.instance = clerk;
    this.subjectState.set(clerk.user?.id ?? null);
    const unsubscribe: () => void = clerk.addListener((resources): void => {
      const previousSubject: string | null = this.subjectState();
      const nextSubject: string | null = resources.session ? resources.user?.id ?? null : null;
      this.subjectState.set(nextSubject);
      if (previousSubject !== null && previousSubject !== nextSubject) {
        // Reset domain stores as well as auth state after cross-tab logout/account changes.
        this.document.location.replace('/login');
      }
    });
    this.destroyRef.onDestroy(unsubscribe);
    return clerk;
  }
}
